import { DatePipe } from '@angular/common';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '@base/base.component';
import { CustomValidators } from '@forms/custom-validators';
import { GetSalesReportQueryResponse } from '@models/reports/reports';
import { KeyValueOptions } from '@models/settings/settings';
import { DealersService } from '@services/dealers.service';
import { ReportsService } from '@services/reports.service';
import { Subscription } from 'rxjs';
import { SalesDetailsModalComponent } from './modals/sales-details-modal/sales-details-modal.component';
import { PERMITION_CONSORTIUM_SALES_PANEL_READ, PERMITION_CONSORTIUM_SALES_PANEL_READ_DETAILS } from 'src/app/const/permitions';
import { Period } from '@models/period';
import { DateHelper } from '@helpers/data-helper';
import { Comissioned } from '@models/comissioned';
import { UsageGraphComponent } from './components/usage-graph/usage-graph.component';

@Component({
  selector: 'app-sales-report',
  templateUrl: './sales-report.component.html',
  styleUrl: './sales-report.component.scss'
})
export class SalesReportComponent extends BaseComponent implements OnDestroy {
  @ViewChild(UsageGraphComponent) usageGraphComponent: UsageGraphComponent;
  
  private _changeAcessPointEvent: Subscription
  private _salesReportSubscription: Subscription;
  private firstSearchDone: boolean = false;

  public sales: GetSalesReportQueryResponse[] = [];

  public dealerOptions: KeyValueOptions[] = []

  public form: FormGroup;
  public loading: boolean = false;

  public constructor(private readonly _fb: FormBuilder,
                     private readonly _datePipe: DatePipe,
                     private readonly _reportsService: ReportsService,
                     private readonly _dealersService: DealersService
                  ) {
                    super();
                  }

  public ngOnInit() {
    this.listDealers();
    this.createForm();

    this._changeAcessPointEvent = this.acessPointChangeEvent().subscribe(() => this.listDealers());

    this._salesReportSubscription = this._reportsService.salesReportStramSubject.subscribe(result => {
      this.sales = result
      this.loading = false;
    });

    if (!this.hasAccessToAnyDealer) {
      this.openDialogMessage("Não foram encontrados nenhum ponto de venda para sua conta, por esse motivo vamos bloquear a pesquisa deste relatório.")
    }
  }

  public ngOnDestroy(): void {
    this._salesReportSubscription.unsubscribe();
    this._changeAcessPointEvent.unsubscribe()
  }

  public get hasAccessToAnyDealer() {
    return this.getAllAllowedDealers().length > 0;
  }

  public get hasPermissionToViewDetails() {
    return this.getPermissions()
               .some(p => [PERMITION_CONSORTIUM_SALES_PANEL_READ_DETAILS].includes(p));
  }

  public get hasPermissionToRead() {
    return this.getPermissions()
               .some(p => [PERMITION_CONSORTIUM_SALES_PANEL_READ].includes(p));
  }

  public viewDetails(sale: GetSalesReportQueryResponse) {
    const { identifierCode, startDate, endDate } = this.form.value;

    const firstPayedInstallmentPeriod: Period = {
      startDate,
      endDate
    };

    this.openDialog(SalesDetailsModalComponent, {data: {
      plataform: sale.plataform,
      identifierCode,
      firstPayedInstallmentPeriod
    }})
  }

  private createForm() {
    this.form = this._fb.group({
      identifierCode: [0, [Validators.nullValidator]],
      startDate: [DateHelper.SubstractDaysFromDate(DateHelper.currentDate, 30), [Validators.required]],
      endDate: [DateHelper.currentDate, [Validators.required]]
    });

    this.form.setValidators([
      CustomValidators.isStartDateGreaterThanEndDate("startDate", "endDate"),
      CustomValidators.periodGreaterThan(30, 'startDate', 'endDate')
    ]);
  }

  public filter(): void {
    try {
      if (!this.hasAccessToAnyDealer || this.form.invalid) {
        return;
      }

      this.loading = true;
      this._reportsService.getSalesReport(this.form.value, this.firstSearchDone);
    } catch(er) {
      this.loading = false;
    } finally {
      this.firstSearchDone = true;
    }
  }

  public async listDealers() {
    const allowedDealersOptions = this.getAllAllowedDealersForCurrentHierachy()
      .filter((item) => item.isEconomicGroup === false)
      .map<KeyValueOptions>(us => ({
        key: `${us.identifierCode} - ${us.description}`,
        value: us.identifierCode
      }));

    this.dealerOptions = [
      {
        key: "Todos",
        value: 0
      }, ...allowedDealersOptions];

      if (this.form) {
        this.form.patchValue({
          identifierCode: 0
        });
      }
  }

  public cleanForm(): void {
    this.form.patchValue({
      identifierCode: 0,
      startDate: DateHelper.SubstractDaysFromDate(DateHelper.currentDate, 30),
      endDate: DateHelper.currentDate
    });

    this.sales = [];
    if (this.usageGraphComponent) {
      this.usageGraphComponent.clearGraphData();
    }
  }



  public aggregateSales(sales: GetSalesReportQueryResponse[]): number {
    return sales.map(s => s.salesCount).reduce((agg, v) => agg + v);
  }
}
